/* ==========================================================================
   Judge.me carousel — Bullantic slider
   Replaces Judge.me's translate3d slider with a native scroll-snap track:
   drag to scroll, snap to card, square arrow buttons, progress bar, keyboard
   support and gentle autoplay. Pairs with bullantic-judgeme.css.
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG = {
    // How wide the carousel runs across the page:
    //   'app'       leave Judge.me's own width setting alone (1080px)
    //   'container' line up with the theme's 1600px sections
    //   'wide'      full screen width, with a comfortable side margin
    //   'full'      edge to edge, no side margin at all
    width: 'wide',

    // Side margin in px for 'wide'. Ignored by the other modes.
    sideMargin: 60,

    // Band behind the reviews:
    //   'navy'   highlighted navy block, white type, gold accents
    //   'cream'  warm stone block, navy type
    //   'light'  no block, sits on the page background
    band: 'navy',

    // Space above and below inside the band, in px. Keep it small.
    paddingY: 40,

    // The eyebrow line above the heading. Set to '' to hide it.
    eyebrow: 'Verified customer reviews',

    // Roughly how wide each review column should be. The number of columns
    // is worked out from this and the available width.
    targetCardWidth: 340,
    maxPerView: 5,

    autoplay: true,
    autoplayDelay: 6000
  };

  /* Theme containers cap width and clip overflow, which traps the band and
     crops its left edge. Walk up from the widget and open up any ancestor
     that is narrower than the viewport — these are the app block's own
     wrappers, not shared theme furniture. */
  function openContainers(wrap) {
    var el = wrap.parentElement;
    var depth = 0;
    var vw = document.documentElement.clientWidth;

    while (el && el !== document.body && depth < 8) {
      var cs = window.getComputedStyle(el);
      if (cs.overflowX === 'hidden' || cs.overflowX === 'clip') {
        el.style.overflowX = 'visible';
      }
      if (el.getBoundingClientRect().width < vw - 1) {
        if (cs.maxWidth !== 'none') el.style.maxWidth = 'none';
        if (cs.paddingLeft !== '0px') el.style.paddingLeft = '0px';
        if (cs.paddingRight !== '0px') el.style.paddingRight = '0px';
      }
      el = el.parentElement;
      depth += 1;
    }
  }

  /* Pin the band to the viewport. Setting an explicit width and then
     measuring the leftover offset is deterministic — relying on auto width
     plus two negative margins depends on every ancestor being centred, which
     is exactly what was pushing the band off to one side. */
  function bleed(wrap) {
    if (CONFIG.width !== 'wide' && CONFIG.width !== 'full') {
      wrap.style.removeProperty('width');
      wrap.style.removeProperty('margin-left');
      wrap.style.removeProperty('margin-right');
      return;
    }

    openContainers(wrap);

    var vw = document.documentElement.clientWidth;

    // Zero everything out, then read where the element actually sits.
    wrap.style.width = vw + 'px';
    wrap.style.marginLeft = '0px';
    wrap.style.marginRight = '0px';

    var left = wrap.getBoundingClientRect().left;

    wrap.style.marginLeft = -Math.round(left) + 'px';
  }

  function applyChrome() {
    document.querySelectorAll('.jdgm-carousel-wrapper').forEach(function (wrap) {
      wrap.dataset.bjWidth = CONFIG.width;
      wrap.dataset.bjBand = CONFIG.band;
      wrap.style.setProperty('--bj-side-margin', CONFIG.sideMargin + 'px');
      wrap.style.setProperty('--bj-pad-y', CONFIG.paddingY + 'px');
      wrap.style.setProperty('--bj-eyebrow', JSON.stringify(CONFIG.eyebrow || ''));
      bleed(wrap);
    });
  }

  var chromeTimer = null;
  window.addEventListener('resize', function () {
    if (chromeTimer) window.clearTimeout(chromeTimer);
    chromeTimer = window.setTimeout(applyChrome, 150);
  });

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Fade each element up as it enters the viewport, staggered along the row. */
  function reveal(nodes, stagger) {
    if (!nodes.length) return;
    if (REDUCE || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var i = parseInt(el.dataset.bjIndex || '0', 10);
        el.style.setProperty('--bj-delay', Math.min(i * (stagger || 90), 600) + 'ms');
        el.classList.add('is-revealed');
        io.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px' });

    nodes.forEach(function (n, i) {
      if (n.dataset.bjReveal) return;
      n.dataset.bjReveal = '1';
      n.dataset.bjIndex = i;
      n.classList.add('bj-reveal');
      io.observe(n);
    });
  }

  /* Count a rating up to its final value the first time it is seen. */
  function countUp(el, target, decimals, write) {
    if (!el || el.dataset.bjCounted) return;
    el.dataset.bjCounted = '1';
    if (REDUCE || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      var start = null;
      var duration = 900;
      function frame(ts) {
        if (start === null) start = ts;
        var p = Math.min(1, (ts - start) / duration);
        var eased = 1 - Math.pow(1 - p, 3);
        write((target * eased).toFixed(decimals));
        if (p < 1) window.requestAnimationFrame(frame);
        else write(target.toFixed(decimals));
      }
      write((0).toFixed(decimals));
      window.requestAnimationFrame(frame);
    }, { threshold: 0.4 });

    io.observe(el);
  }

  function build(carousel) {
    if (!carousel || carousel.dataset.bjSlider === 'on') return;

    var track = carousel.querySelector('.jdgm-carousel__item-container');
    var wrapper = carousel.querySelector('.jdgm-carousel__item-wrapper');
    if (!track || !wrapper) return;

    var items = wrapper.querySelectorAll('.jdgm-carousel-item');
    if (!items.length) return;

    carousel.dataset.bjSlider = 'on';

    // Judge.me positions the strip with an inline transform. The stylesheet
    // neutralises it with !important; clear it here too so the first paint
    // starts at zero rather than mid-strip.
    wrapper.style.transform = 'none';

    // ---- controls -------------------------------------------------------
    var nav = document.createElement('div');
    nav.className = 'bj-nav';
    nav.innerHTML =
      '<div class="bj-nav__progress"><span></span></div>' +
      '<div class="bj-nav__count"></div>' +
      '<div class="bj-nav__buttons">' +
      '<button type="button" class="bj-arrow bj-arrow--prev" aria-label="Previous reviews">' + ARROW_LEFT + '</button>' +
      '<button type="button" class="bj-arrow bj-arrow--next" aria-label="Next reviews">' + ARROW_RIGHT + '</button>' +
      '</div>';

    var stale = carousel.parentNode.querySelector('.bj-nav');
    if (stale) stale.parentNode.removeChild(stale);
    carousel.parentNode.insertBefore(nav, carousel.nextSibling);

    var prev = nav.querySelector('.bj-arrow--prev');
    var next = nav.querySelector('.bj-arrow--next');
    var fill = nav.querySelector('.bj-nav__progress span');
    var count = nav.querySelector('.bj-nav__count');

    // Divide the visible track into whole columns so the cards line up with
    // the rails above and below, instead of inheriting Judge.me's 25% width
    // (which is indeterminate inside a max-content strip).
    function perView(w) {
      if (w < 600) return 1;
      var n = Math.round(w / (CONFIG.targetCardWidth || 340));
      return Math.min(CONFIG.maxPerView, Math.max(2, n));
    }

    function sizeItems() {
      var w = track.clientWidth;
      if (!w) return;
      var width = w < 600 ? w * 0.86 : w / perView(w);
      carousel.style.setProperty('--bj-item-w', width.toFixed(2) + 'px');
    }

    function step() {
      var first = wrapper.querySelector('.jdgm-carousel-item');
      return first ? first.getBoundingClientRect().width : track.clientWidth;
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function update() {
      var max = maxScroll();
      var ratio = max > 0 ? track.scrollLeft / max : 0;
      var perView = Math.max(1, Math.round(track.clientWidth / step()));
      var pages = Math.max(1, Math.ceil(items.length / perView));
      var page = Math.min(pages, Math.round(ratio * (pages - 1)) + 1);

      fill.style.width = (100 / pages) * page + '%';
      count.textContent = page + ' / ' + pages;

      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
    }

    function scrollByStep(dir) {
      track.scrollBy({ left: step() * dir, behavior: 'smooth' });
    }

    prev.addEventListener('click', function () {
      pause();
      scrollByStep(-1);
    });

    next.addEventListener('click', function () {
      pause();
      scrollByStep(1);
    });

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        sizeItems();
        update();
      }, 150);
    });

    // ---- keyboard -------------------------------------------------------
    track.setAttribute('tabindex', '0');
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', 'Customer reviews');
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        pause();
        scrollByStep(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        pause();
        scrollByStep(-1);
      }
    });

    // ---- drag to scroll (pointer devices) -------------------------------
    var down = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return; // native touch scrolling is better
      down = true;
      moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
      track.style.cursor = 'grabbing';
      pause();
    });

    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var delta = e.clientX - startX;
      if (Math.abs(delta) > 3) moved = true;
      track.scrollLeft = startScroll - delta;
    });

    function endDrag() {
      if (!down) return;
      down = false;
      track.style.scrollBehavior = '';
      track.style.cursor = '';
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    // Swallow the click that ends a drag so it doesn't open a link
    track.addEventListener(
      'click',
      function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      },
      true
    );

    // ---- autoplay -------------------------------------------------------
    var timer = null;
    var stopped = false;

    function tick() {
      if (document.hidden) return;
      if (track.scrollLeft >= maxScroll() - 2) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollByStep(1);
      }
    }

    function play() {
      if (!CONFIG.autoplay || stopped || timer) return;
      timer = window.setInterval(tick, CONFIG.autoplayDelay);
    }

    function pause() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    carousel.addEventListener('mouseenter', pause);
    carousel.addEventListener('mouseleave', play);
    carousel.addEventListener('focusin', pause);
    nav.addEventListener('mouseenter', pause);
    nav.addEventListener('mouseleave', play);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      stopped = true;
    }

    // Only autoplay while the carousel is actually on screen
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) play();
          else pause();
        });
      }, { threshold: 0.25 }).observe(carousel);
    } else {
      play();
    }

    sizeItems();
    update();
    window.setTimeout(function () {
      sizeItems();
      update();
    }, 400);

    // ---- read more --------------------------------------------------------
    function setupReadMore() {
      wrapper.querySelectorAll('.jdgm-carousel-item').forEach(function (item) {
        var body = item.querySelector('.jdgm-carousel-item__review-body');
        var content = item.querySelector('.jdgm-carousel-item__review-content');
        if (!body || !content) return;

        var existing = content.querySelector('.bj-more');
        var overflows = body.scrollHeight - body.clientHeight > 4;

        if (!overflows) {
          if (existing && !item.classList.contains('bj-expanded')) existing.remove();
          return;
        }
        if (existing) return;

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bj-more';
        btn.textContent = 'Read more';
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          pause();
          var open = item.classList.toggle('bj-expanded');
          btn.textContent = open ? 'Read less' : 'Read more';
          window.setTimeout(update, 260);
        });
        content.appendChild(btn);
      });
    }

    // ---- click / drag the progress line to seek ---------------------------
    function seek(clientX) {
      var rect = fill.parentNode.getBoundingClientRect();
      var pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      track.scrollTo({ left: maxScroll() * pct, behavior: 'smooth' });
    }

    var seeking = false;
    fill.parentNode.addEventListener('pointerdown', function (e) {
      seeking = true;
      pause();
      seek(e.clientX);
    });
    window.addEventListener('pointermove', function (e) {
      if (seeking) seek(e.clientX);
    });
    window.addEventListener('pointerup', function () {
      seeking = false;
    });

    // ---- reveal + score count --------------------------------------------
    reveal(Array.prototype.slice.call(wrapper.querySelectorAll('.jdgm-carousel-item')), 90);

    var scoreEl = carousel.parentNode.querySelector('.jdgm-all-reviews-rating[data-score]');
    if (scoreEl) {
      var score = parseFloat(scoreEl.getAttribute('data-score'));
      if (!isNaN(score)) {
        countUp(scoreEl, score, 2, function (v) {
          scoreEl.setAttribute('data-score', v);
        });
      }
    }

    setupReadMore();
    window.setTimeout(setupReadMore, 600);
    window.addEventListener('resize', function () {
      window.setTimeout(setupReadMore, 200);
    });


  }


  /* ------------------------------------------------------------------------
     Product page — Judge.me's newer Vue widget. Its masonry items carry an
     inline transform for positioning, so anything animated has to happen on
     the inner element or the layout jumps.
     ------------------------------------------------------------------------ */
  function enhanceProductWidget() {
    var inners = document.querySelectorAll(
      '.jdgm-masonry-grid__masonry-item-inner, .jdgm-review-widget .jdgm-review-card'
    );
    reveal(Array.prototype.slice.call(inners), 70);

    document.querySelectorAll('.jm-average-rating-display').forEach(function (block) {
      var el = block.querySelector('.jm-cluster > p.jm-text');
      if (!el || el.dataset.bjCounted) return;
      var raw = (el.textContent || '').trim();
      var value = parseFloat(raw);
      if (isNaN(value)) return;
      var decimals = raw.indexOf('.') > -1 ? raw.split('.')[1].length : 0;
      countUp(el, value, decimals, function (v) {
        el.textContent = v;
      });
    });
  }

  function scan() {
    applyChrome();
    document.querySelectorAll('.jdgm-carousel').forEach(build);
    enhanceProductWidget();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Judge.me renders asynchronously and re-renders on some interactions,
  // so keep watching for a carousel that hasn't been wired up yet.
  if ('MutationObserver' in window) {
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        scan();
      }, 200);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.addEventListener('load', scan);
})();
